import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { array, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { inject as service } from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import { TYPING_DEBOUNCE_MS } from 'volta/components/bv-app-search';
import BvLabelled from 'volta/components/bv-labelled';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import { DynamicDatePrefix } from 'volta/models/constants/date-filters';
import { parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { scalarEquals } from 'volta/utils/array-utils';
import FiltersBooleanFilter from '../boolean-filter';
import FiltersComparatorSelect from '../comparator-select';
import type { IntlService } from 'ember-intl';
import type { IResourceName } from 'volta/models/base-model';
import type LabelService from 'volta/services/label-service';
import type { TComparatorOption } from '../comparator-select';
interface FiltersBaseFilterSignature {
    Args: {
        filterValues?: TFilterValues;
        allowClear?: boolean;
        resourceName: IResourceName;
        initialValue?: TFilterValue;
        definition: TFilterDefinition;
        onChange: (filterValue: TFilterValue, field: string) => void;
        clearDefaultValue?: string[];
    };
}
export default class FiltersBaseFilterComponent extends Component<FiltersBaseFilterSignature> {
    /**
   * Services
   */ @service
    intl: IntlService;
    @service
    labelService: LabelService;
    /**
   * Tracked properties
   */ @tracked
    enabled: boolean = false;
    @tracked
    values: string[] = [];
    @tracked
    comparator?: TComparator;
    /**
   * Getters
   */ get isEmptyComparator() {
        return this.comparator === 'is_empty';
    }
    get filterComponent() {
        if (this.args.definition.component) {
            return ensureSafeComponent(this.args.definition.component, this);
        } else {
            return undefined;
        }
    }
    get hasSingleComparator() {
        if (!this.args.definition) {
            return true;
        }
        return this.args.definition.comparators.length < 2;
    }
    get isMultipleComparator() {
        return this.checkIsMultipleComparator(this.comparator);
    }
    get firstComparator() {
        const { comparators } = this.args.definition ?? {};
        if (!comparators) {
            return undefined;
        }
        return comparators.length > 0 ? comparators[0] : undefined;
    }
    @cached
    get title() {
        const { customFieldDefinition, queryParam, title } = this.args.definition ?? {};
        const labelKey = customFieldDefinition ? parseCFApiParamKey(queryParam) : queryParam;
        return customFieldDefinition ? this.labelService.tOrElse(labelKey, title) : title ?? this.labelService.tOrElse((queryParam && 'filterDefinitions.' + queryParam) as string, this.intl.t(queryParam!));
    }
    /**
   * Actions
   */ @action
    handleInitialValueChange(_element: HTMLElement, [initialValue]: [TFilterValue | undefined]) {
        this.initValues(initialValue);
    }
    valuesDidChange = restartableTask(async (values?: string[])=>{
        this.enableClear(values);
        this.values = values ?? [];
        const filterValue = this.buildFilterValue();
        if (filterValue) {
            await timeout(TYPING_DEBOUNCE_MS);
            this.args.onChange?.(filterValue, this.args.definition.queryParam);
        }
    });
    @action
    selectComparator(newComparator: TComparatorOption) {
        const oldComparator = this.comparator;
        if (oldComparator !== newComparator.key) {
            this.checkDynamicFilter(newComparator);
            const wasMultiple = this.isMultipleComparator;
            const isMultiple = this.checkIsMultipleComparator(newComparator.key);
            // Only keep the first value if we're passing
            // from multiple to single value
            if (wasMultiple && !isMultiple) {
                this.values = this.values.slice(0, 1);
            }
            // Make sure bt filter has 2 values
            if (newComparator.key === 'bt' && this.values.length === 1) {
                this.values = [
                    this.values[0]!,
                    this.values[0]!
                ];
            }
            if (newComparator.key === 'is_empty') {
                this.values = [
                    'true'
                ];
            }
            if (oldComparator === 'is_empty') {
                this.values = [];
            }
            this.comparator = newComparator.key;
            this.enableClear(this.values);
            const filterValue = this.buildFilterValue();
            if (filterValue) {
                this.args.onChange?.(filterValue, this.args.definition.queryParam);
            }
        }
    }
    private checkDynamicFilter(newComparator?: TComparatorOption) {
        if (this.values[0]?.startsWith(DynamicDatePrefix) && newComparator?.key == 'bt') {
            this.values = [];
        }
    }
    @action
    initValues(initialValue?: TFilterValue) {
        this.enableClear(initialValue?.values);
        this.values = (initialValue?.values ?? []) as string[];
        this.comparator = initialValue?.comparator ?? this.comparator ?? this.firstComparator;
    }
    /**
   * Private utils
   */ private checkIsMultipleComparator(comparator?: TComparator) {
        return comparator && [
            'in',
            'ni',
            'all',
            'any',
            'bt'
        ].includes(comparator);
    }
    private buildFilterValue(): TFilterValue | undefined {
        const comparator = this.comparator ?? this.firstComparator;
        return comparator ? {
            comparator,
            values: this.values,
            enabled: this.enabled
        } : undefined;
    }
    private enableClear(values?: string[]): void {
        const { clearDefaultValue } = this.args;
        if (clearDefaultValue) {
            this.enabled = !scalarEquals(values, clearDefaultValue);
        } else {
            this.enabled = values ? values.length > 0 : false;
        }
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{! template-lint-disable no-at-ember-render-modifiers }}
    <div
      class={{classNames (if this.enabled 'is-enabled')}}
      {{didInsert (fn this.initValues @initialValue)}}
      {{didUpdate this.handleInitialValueChange @initialValue}}
    >
      <BvLabelled
        @label={{this.title}}
        @action={{if
          (and this.enabled this.values.length (not @definition.required))
          (hash
            onClick=(perform this.valuesDidChange (orElse @clearDefaultValue (array)))
            text=(t 'clear')
          )
        }}
        @dataTestLabelled={{@definition.queryParam}}
      >
        <div class='u-d-flex u-align-items-center'>
          {{#unless this.hasSingleComparator}}
            <FiltersComparatorSelect
              @comparators={{@definition.comparators}}
              @selected={{this.comparator}}
              @onChange={{this.selectComparator}}
              @name={{@definition.queryParam}}
            />
          {{/unless}}
          {{#if (or this.isEmptyComparator this.filterComponent)}}
            <div class='u-d-flex u-flex-grow-1 u-align-items-center'>
              {{#if this.isEmptyComparator}}
                {{! @glint-ignore}}
                <FiltersBooleanFilter
                  @yesLabel={{t 'bvFilters.isEmpty'}}
                  @noLabel={{t 'bvFilters.hasValue'}}
                  @fullWidth={{true}}
                  @values={{this.values}}
                  @comparator='is_empty'
                  @onChange={{perform this.valuesDidChange}}
                  @name={{@definition.queryParam}}
                  class='u-flex-fill'
                />
              {{else}}
                {{#let
                  (component
                    this.filterComponent
                    filterValues=@filterValues
                    values=this.values
                    definition=@definition
                    resourceName=@resourceName
                    multiple=this.isMultipleComparator
                    allowClear=@allowClear
                    comparator=this.comparator
                    onChange=(perform this.valuesDidChange)
                    name=@definition.queryParam
                    componentArgs=@definition.componentArgs
                    required=@definition.required
                  )
                  as |Definition|
                }}
                  <Definition />
                {{/let}}
              {{/if}}
            </div>
          {{/if}}
        </div>
      </BvLabelled>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        'Filters::BaseFilter': typeof FiltersBaseFilterComponent;
        'filters/base-filter': typeof FiltersBaseFilterComponent;
    }
}
